"use client";

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, PowerOff, RotateCcw, X } from "lucide-react";
import { useAdminSession } from "@/hooks/use-admin-session";
import { PHASE_KEYS, PHASE_TITLES, type PhaseKey } from "@/lib/hundert-tage";
import {
  listModuleChapters,
  saveModuleChapter,
  setModuleChapterActive,
} from "@/lib/module-chapters.functions";
import type { ModuleChapter } from "@/lib/module-chapters";
import { CHAPTER_SECTION_TITLES } from "@/lib/module-chapters";
import {
  FEHLER_BLOCK_HEADING,
  FEHLER_BLOCK_SNIPPET,
  produktBlockSnippet,
} from "@/lib/module-chapters/parseChapter";
import { checkChapterProductKeys } from "@/lib/product-cards.functions";
import { PRODUCT_KEY_STATUS_LABELS } from "@/lib/product-cards";
import { MarkdownEditor } from "@/components/MarkdownEditor";
import { ChapterBody } from "@/components/cockpit/ChapterBody";

export const Route = createFileRoute("/_admin/admin/module-chapters")({
  head: () => ({
    meta: [
      { title: "Kapitel 100 Tage – Admin – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ModuleChaptersAdminPage,
});

type FormState = {
  id?: string;
  chapter_number: string;
  slug: string;
  title: string;
  body: string;
  phase_key: PhaseKey | "";
  sort_order: string;
};

const emptyForm: FormState = {
  chapter_number: "1",
  slug: "",
  title: "",
  body: "",
  phase_key: "",
  sort_order: "0",
};

const inputClass =
  "mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary";
const labelClass = "block text-sm font-semibold text-foreground";

function ModuleChaptersAdminPage() {
  const session = useAdminSession();
  const isAdmin = session.isAdmin;
  const qc = useQueryClient();

  const listFn = useServerFn(listModuleChapters);
  const saveFn = useServerFn(saveModuleChapter);
  const activeFn = useServerFn(setModuleChapterActive);

  const [form, setForm] = useState<FormState | null>(null);

  const { data: rows, isLoading, error } = useQuery({
    queryKey: ["admin", "module-chapters"],
    queryFn: () => listFn(),
  });

  const grouped = useMemo(() => {
    const all = rows ?? [];
    const fahrplan = PHASE_KEYS.map((key) => all.filter((r) => r.phase_key === key))
      .flat()
      .filter(Boolean);
    const nachschlagen = all
      .filter((r) => !r.phase_key)
      .sort((a, b) => a.chapter_number - b.chapter_number);
    return { fahrplan, nachschlagen };
  }, [rows]);

  const save = useMutation({
    mutationFn: (input: FormState) =>
      saveFn({
        data: {
          id: input.id,
          chapter_number: Number(input.chapter_number),
          slug: input.slug.trim(),
          title: input.title.trim(),
          body: input.body,
          phase_key: input.phase_key === "" ? null : input.phase_key,
          sort_order: Number(input.sort_order) || 0,
        },
      }),
    onSuccess: () => {
      toast.success("Kapitel gespeichert.");
      setForm(null);
      void qc.invalidateQueries({ queryKey: ["admin", "module-chapters"] });
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Speichern fehlgeschlagen."),
  });

  const toggle = useMutation({
    mutationFn: (input: { id: string; active: boolean }) => activeFn({ data: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "module-chapters"] });
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Änderung fehlgeschlagen."),
  });

  function edit(row: ModuleChapter) {
    setForm({
      id: row.id,
      chapter_number: String(row.chapter_number),
      slug: row.slug,
      title: row.title,
      body: row.body,
      phase_key: row.phase_key ?? "",
      sort_order: String(row.sort_order),
    });
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-foreground">
            Kapitel 100 Tage
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Lesekapitel des Moduls „100 Tage im Amt". Kapitel werden nicht gelöscht,
            sondern deaktiviert. Deaktivierte Kapitel sind im Cockpit nicht erreichbar.
          </p>
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={() => setForm({ ...emptyForm })}
            className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 font-display text-sm font-extrabold text-brand-button-foreground"
          >
            <Plus className="h-4 w-4" aria-hidden /> Neues Kapitel
          </button>
        )}
      </header>

      {!isAdmin && (
        <p className="rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
          Als Editorin oder Editor können Sie die Kapitel nur ansehen. Änderungen sind
          Administratoren vorbehalten.
        </p>
      )}

      {form && isAdmin && (
        <section className="space-y-4 rounded-lg border border-border bg-background p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-extrabold text-foreground">
              {form.id ? "Kapitel bearbeiten" : "Kapitel anlegen"}
            </h2>
            <button
              type="button"
              onClick={() => setForm(null)}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Formular schließen"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className={labelClass}>
              Kapitelnummer (1 bis 9)
              <input
                type="number"
                min={1}
                max={9}
                className={inputClass}
                value={form.chapter_number}
                onChange={(e) => setForm({ ...form, chapter_number: e.target.value })}
              />
            </label>
            <label className={labelClass}>
              Sortierung
              <input
                type="number"
                min={0}
                className={inputClass}
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
              />
            </label>
            <label className={labelClass}>
              Titel
              <input
                className={inputClass}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </label>
            <label className={labelClass}>
              Adressbaustein (Slug)
              <input
                className={inputClass}
                value={form.slug}
                placeholder="z-b-amtsstart-vorbereiten"
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
              />
            </label>
            <label className={labelClass}>
              Phase (optional)
              <select
                className={inputClass}
                value={form.phase_key}
                onChange={(e) =>
                  setForm({ ...form, phase_key: e.target.value as PhaseKey | "" })
                }
              >
                <option value="">Ohne Phasenbezug (Zum Nachschlagen)</option>
                {PHASE_KEYS.map((key) => (
                  <option key={key} value={key}>
                    {PHASE_TITLES[key]}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div>
            <span className={labelClass}>Kapiteltext</span>
            <div className="mt-1">
              <MarkdownEditor
                value={form.body}
                onChange={(v) => setForm({ ...form, body: v })}
                extraSnippets={[
                  {
                    label: FEHLER_BLOCK_HEADING,
                    title: `Hinweiskasten „${FEHLER_BLOCK_HEADING}" einfügen`,
                    snippet: FEHLER_BLOCK_SNIPPET,
                  },
                  {
                    label: "Produktkarte",
                    title: "Produktkarte mit Kennung einer Leistung einfügen",
                    snippet: produktBlockSnippet(),
                  },
                ]}
                renderPreview={(v) => <ChapterBody body={v} showProductPlaceholders />}
              />
            </div>
            <ProductKeyHints body={form.body} />
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              disabled={save.isPending}
              onClick={() => save.mutate(form)}
              className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 font-display text-sm font-extrabold text-brand-button-foreground disabled:opacity-60"
            >
              {save.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
              Speichern
            </button>
            <button
              type="button"
              onClick={() => setForm(null)}
              className="rounded-md border border-border px-4 py-2 text-sm font-semibold"
            >
              Abbrechen
            </button>
          </div>
        </section>
      )}

      {isLoading && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Kapitel werden geladen …
        </p>
      )}
      {error && (
        <p className="text-sm text-destructive">Die Kapitel konnten nicht geladen werden.</p>
      )}

      {rows && (
        <div className="space-y-6">
          <ChapterGroup
            title={CHAPTER_SECTION_TITLES.fahrplan}
            rows={grouped.fahrplan}
            isAdmin={isAdmin}
            onEdit={edit}
            onToggle={(id, active) => toggle.mutate({ id, active })}
          />
          <ChapterGroup
            title={CHAPTER_SECTION_TITLES.nachschlagen}
            rows={grouped.nachschlagen}
            isAdmin={isAdmin}
            onEdit={edit}
            onToggle={(id, active) => toggle.mutate({ id, active })}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Ticket Z12: Prüfhinweis zu den verwendeten Kennungen. Nicht auflösbare oder
 * deaktivierte Kennungen erzeugen im Cockpit keine Karte – hier wird das
 * gemeldet, damit es nicht unbemerkt bleibt.
 */
function ProductKeyHints({ body }: { body: string }) {
  const checkFn = useServerFn(checkChapterProductKeys);
  const { data } = useQuery({
    queryKey: ["admin-product-keys", body],
    queryFn: () => checkFn({ data: { body } }),
  });
  if (!data || data.length === 0) return null;
  const problems = data.filter((c) => c.status !== "ok");

  return (
    <div className="mt-3 space-y-1 rounded-md border border-border bg-muted/40 p-3 text-xs">
      <p className="font-semibold text-foreground">Produktkarten in diesem Kapitel</p>
      <ul className="space-y-0.5">
        {data.map((check) => (
          <li
            key={check.serviceKey}
            className={check.status === "ok" ? "text-muted-foreground" : "text-destructive"}
          >
            <code className="font-mono">{check.serviceKey}</code>
            {" – "}
            {check.status === "ok"
              ? (check.title ?? "")
              : PRODUCT_KEY_STATUS_LABELS[check.status]}
          </li>
        ))}
      </ul>
      {problems.length > 0 && (
        <p className="text-muted-foreground">
          Karten mit Beanstandung erscheinen im Cockpit nicht.
        </p>
      )}
    </div>
  );
}

function ChapterGroup({
  title,
  rows,
  isAdmin,
  onEdit,
  onToggle,
}: {
  title: string;
  rows: ModuleChapter[];
  isAdmin: boolean;
  onEdit: (row: ModuleChapter) => void;
  onToggle: (id: string, active: boolean) => void;
}) {
  return (
    <section className="space-y-2">
      <h2 className="font-display text-lg font-extrabold text-foreground">{title}</h2>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Noch keine Kapitel angelegt.</p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border bg-background">
          {rows.map((row) => (
            <li key={row.id} className="flex flex-wrap items-center gap-3 p-4">
              <span className="min-w-0 flex-1">
                <span className="block text-xs text-muted-foreground">
                  Kapitel {row.chapter_number} ·{" "}
                  {row.phase_key ? PHASE_TITLES[row.phase_key] : "ohne Phasenbezug"} · /
                  {row.slug}
                </span>
                <span className="mt-0.5 block text-sm font-semibold text-foreground">
                  {row.title}
                </span>
              </span>
              {!row.active && (
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                  deaktiviert
                </span>
              )}
              {isAdmin && (
                <span className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onEdit(row)}
                    className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-semibold"
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden /> Bearbeiten
                  </button>
                  <button
                    type="button"
                    onClick={() => onToggle(row.id, !row.active)}
                    className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-semibold"
                  >
                    {row.active ? (
                      <>
                        <PowerOff className="h-3.5 w-3.5" aria-hidden /> Deaktivieren
                      </>
                    ) : (
                      <>
                        <RotateCcw className="h-3.5 w-3.5" aria-hidden /> Aktivieren
                      </>
                    )}
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
